import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import {
  ArrowRight, CheckCircle, Globe2, DollarSign, Shield, Heart,
  Plane, Languages, Star, MapPin
} from "lucide-react";

const IMAGES = {
  globe: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/157d58af0c873689086ab59e871ccbef313009bf79a9876c49d9d65e611c1cda.png",
  hospital: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/5d6614bac40beeed91e374e34471c05cfd78e519778e0ce71bcb8693e85a8054.png",
  doctor: "https://images.unsplash.com/photo-1758691462126-2ee47c8bf9e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxjYXJpbmclMjBkb2N0b3IlMjBwYXRpZW50fGVufDB8fHx8MTc3NjI0ODExOXww&ixlib=rb-4.1.0&q=85",
};

const targetCountries = [
  { region: "GCC", countries: ["UAE", "Saudi Arabia", "Qatar", "Kuwait", "Oman", "Bahrain"] },
  { region: "Africa", countries: ["Nigeria", "Kenya", "Uganda", "Tanzania", "Ethiopia", "Ghana", "South Africa"] },
];

const benefits = [
  { icon: DollarSign, title: "Save 60-90% on Treatment", desc: "World-class medical care at a fraction of costs in your home country." },
  { icon: Shield, title: "JCI & NABH Accredited", desc: "Our partner hospitals meet the highest international quality standards." },
  { icon: Languages, title: "Multilingual Support", desc: "Assistance in English, Arabic, French, German, and Hindi." },
  { icon: Plane, title: "Complete Travel Support", desc: "Visa, flights, accommodation, and local transport - all arranged for you." },
  { icon: Heart, title: "Kerala & Ayurveda", desc: "Access traditional Ayurvedic treatments alongside modern medicine in Kerala." },
  { icon: Globe2, title: "End-to-End Care", desc: "From first consultation to post-treatment follow-up in your home country." },
];

const testimonials = [
  { name: "Ahmed K.", country: "UAE", treatment: "Heart Bypass Surgery", quote: "The coordination was exceptional. My surgeon was one of the best in Kerala, India." },
  { name: "Amina O.", country: "Nigeria", treatment: "Hip Replacement", quote: "I saved over 80% compared to costs back home. The care was outstanding." },
  { name: "Fatima M.", country: "Saudi Arabia", treatment: "Fertility Treatment", quote: "The team made everything seamless - from visa to treatment to recovery." },
  { name: "James W.", country: "Uganda", treatment: "Spine Surgery", quote: "World-class treatment at affordable prices. I recommend Flarial to everyone." },
];

const costData = [
  ["Heart Bypass", "$5,000", "$70,000+", "$50,000+"],
  ["Hip Replacement", "$6,000", "$45,000+", "$35,000+"],
  ["Knee Replacement", "$5,500", "$40,000+", "$30,000+"],
  ["IVF Treatment", "$3,000", "$20,000+", "$12,000+"],
  ["Ayurveda Program", "$1,500", "N/A", "N/A"],
];

export default function LandingPage() {
  return (
    <div data-testid="landing-page">
      <SEO title="Medical Tourism for GCC & Africa" description="Affordable world-class healthcare in Kerala, India for patients from UAE, Saudi Arabia, Nigeria, Kenya, Uganda. Save 60-90% on medical treatments and Ayurveda." path="/for/international" keywords="medical tourism GCC, medical tourism Africa, healthcare India UAE, treatment Nigeria India, medical travel Saudi Arabia" />
      {/* Hero */}
      <section className="relative section-padding overflow-hidden" style={{ background: "linear-gradient(135deg, #03045e 0%, #00A4A9 50%, #00b4d8 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <Badge className="mb-6 bg-white/20 text-white hover:bg-white/20 border-0 rounded-full px-4 py-1.5 text-xs font-semibold">
                SERVING PATIENTS FROM GCC & AFRICA
              </Badge>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-tight mb-6">
                Affordable World-Class Healthcare in{" "}
                <span className="font-medium text-[#90e0ef]">India</span>
              </h1>
              <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
                Patients from the Gulf and Africa trust us for safe, affordable medical treatments
                and traditional Ayurveda therapies in Kerala's finest hospitals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/contact" data-testid="landing-cta-btn">
                  <Button size="lg" className="rounded-full bg-white text-[#03045e] hover:bg-white/90 px-8 h-12 text-base font-semibold btn-hover">
                    Get Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={buildWhatsAppUrl("Hi, I'd like to learn more about Flarial MediTours.")} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-btn">
                  <Button size="lg" variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 h-12 text-base">
                    WhatsApp Us
                  </Button>
                </a>
              </div>
              <div className="flex gap-8">
                {[["5,000+", "Patients"], ["50+", "Hospitals"], ["15+", "Countries"]].map(([n, l]) => (
                  <div key={l}>
                    <div className="font-outfit text-2xl font-semibold text-white">{n}</div>
                    <div className="text-xs text-white/60 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <img src={IMAGES.globe} alt="Global medical tourism" className="rounded-3xl w-full" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Countries We Serve */}
      <section className="section-padding bg-white" data-testid="countries-section">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="overline">COUNTRIES WE SERVE</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                Trusted by Patients Across the Globe
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {targetCountries.map((region, i) => (
              <FadeIn key={region.region} delay={i * 100}>
                <div className="bg-[#F9F9F8] rounded-2xl p-8" data-testid={`region-${region.region.toLowerCase()}`}>
                  <h3 className="font-outfit text-xl font-medium text-[#03045e] mb-4 flex items-center gap-2">
                    <Globe2 className="w-5 h-5 text-[#00A4A9]" /> {region.region} Region
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {region.countries.map((c) => (
                      <Badge key={c} variant="outline" className="rounded-full px-3 py-1 text-sm border-[#00A4A9]/30 text-[#03045e]">
                        <MapPin className="w-3 h-3 mr-1 text-[#00A4A9]" /> {c}
                      </Badge>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="overline">WHY CHOOSE INDIA</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                The Smart Choice for Medical Care
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <FadeIn key={b.title} delay={i * 80}>
                <div className="bg-white border border-slate-200 rounded-2xl p-7 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`benefit-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#e0f2fe] flex items-center justify-center mb-4">
                    <b.icon className="w-6 h-6 text-[#00A4A9]" />
                  </div>
                  <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{b.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="section-padding bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-10">
              <span className="overline">COST COMPARISON</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                See How Much You Can Save
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="landing-cost-table">
                <thead>
                  <tr className="bg-[#03045e] text-white">
                    <th className="text-left p-4 rounded-tl-xl font-outfit font-medium">Procedure</th>
                    <th className="text-center p-4 font-outfit font-medium">India</th>
                    <th className="text-center p-4 font-outfit font-medium">GCC</th>
                    <th className="text-center p-4 rounded-tr-xl font-outfit font-medium">UK/US</th>
                  </tr>
                </thead>
                <tbody>
                  {costData.map(([proc, india, gcc, west], i) => (
                    <tr key={proc} className={i % 2 === 0 ? "bg-[#F9F9F8]" : "bg-white"}>
                      <td className="p-4 text-sm font-medium text-[#03045e]">{proc}</td>
                      <td className="p-4 text-center text-sm font-semibold text-[#00A4A9]">{india}</td>
                      <td className="p-4 text-center text-sm text-slate-500">{gcc}</td>
                      <td className="p-4 text-center text-sm text-slate-500">{west}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="overline">PATIENT STORIES</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                What Our International Patients Say
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <FadeIn key={t.name} delay={i * 80}>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 card-hover" data-testid={`landing-testimonial-${i}`}>
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-sm text-slate-600 italic mb-4">"{t.quote}"</p>
                  <div className="border-t border-slate-100 pt-3">
                    <div className="font-outfit font-medium text-[#03045e] text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.country} - {t.treatment}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: "linear-gradient(135deg, #03045e 0%, #00A4A9 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-white mb-5 tracking-tight">
              Start Your Treatment Journey Today
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              Join thousands of patients from the GCC and Africa who chose India for world-class, affordable healthcare.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="landing-bottom-cta">
                <Button size="lg" className="rounded-full bg-white text-[#03045e] hover:bg-white/90 px-8 h-12 font-semibold btn-hover">
                  Get Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex justify-center gap-6 text-sm text-white/60">
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#90e0ef]" /> Free Consultation</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#90e0ef]" /> No Visa Hassle</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#90e0ef]" /> 48hr Response</span>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
